import React, { useState, useEffect } from "react";
import "./input.css";
import Button from "../button/Button";
import { host } from "../../constants/backendpath";
import axios from "axios";
import Alert from "../alert/Alert";
import { useNavigate } from "react-router-dom";

const Input = ({ add, clicked, click, appln_no, collegeCode, branchCode, admin }) => {
  const [input, setInput] = useState("");
  const [touched, setTouched] = useState(false);
  const [visibleIndexes, setVisibleIndexes] = useState([]);
  const [count, setCount] = useState(null);
  const navigate = useNavigate();

  // ✅ get college code (priority: localStorage → prop)
  const savedCollegeCode = localStorage.getItem("collegeCode");
  const effectiveCollegeCode = savedCollegeCode || collegeCode;

  // ✅ fetch student count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const body = { b_code: branchCode };
        if (admin) body.collegeCode = effectiveCollegeCode;

        const res = await axios.post(`${host}student-count`, body, { withCredentials: true });
        if (res.status === 200) setCount(res.data.count);
        else setCount(0);
      } catch (err) {
        if (err.response?.status === 401) navigate("/");
        else setCount(0);
      }
    };

    if (effectiveCollegeCode && branchCode) fetchCount();
  }, [effectiveCollegeCode, branchCode, admin, navigate]);

  // ✅ expected application number
  const expectedAppln =
    count !== null
      ? `${effectiveCollegeCode}${branchCode}26${String(count + 1).padStart(3, "0")}`
      : "";

  // ✅ alert system
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertOkAction, setAlertOkAction] = useState(() => () => {});

  // ✅ validation rules
  const validations = [
    {
      key: "collegeCode",
      text: `College Code must be ${effectiveCollegeCode}`,
      check: (val) => new RegExp(`^${effectiveCollegeCode}`).test(val),
    },
    {
      key: "branchCode",
      text: `Branch Code must be ${branchCode}`,
      check: (val) => new RegExp(`^${effectiveCollegeCode}${branchCode}`).test(val),
    },
    {
      key: "year26",
      text: "Must contain '26' after branch code (for year 2026)",
      check: (val) => new RegExp(`^${effectiveCollegeCode}${branchCode}26`).test(val),
    },
    {
      key: "uniqueNumber",
      text: `Last three digits must be ${String(count + 1).padStart(3, "0")}`,
      check: (val) => {
        const match = val.match(/\d{2}(\d{3})$/);
        return match && parseInt(match[1], 10) === count + 1;
      },
    },
  ];

  // ✅ input handlers
  const handleChange = (e) => {
    setInput(e.target.value.toUpperCase());
  };

  const handleFocus = () => {
    if (!touched) {
      setTouched(true);
      validations.forEach((_, index) => {
        setTimeout(() => {
          setVisibleIndexes((prev) => [...prev, index]);
        }, index * 400);
      });
    }
  };

  // ✅ cancel
  const handleCancel = () => {
    add(false);
  };

  // ✅ confirm
  const handleConfirm = async () => {
    const allValid = validations.every((rule) => rule.check(input));
    if (!allValid) {
      alert("Please fix validation errors before confirming.");
      return;
    }

    try {
      const body = { appln_no: input };
      if (admin) body.collegeCode = effectiveCollegeCode;

      const result = await axios.post(`${host}checkApplnNo`, body, { withCredentials: true });

      if (result.data.valid) {
        appln_no(input);
        add(false);
        clicked(2);
      } else {
        setShowAlert(true);
        setAlertMessage("Application Number Already Exists");
        setAlertType("warning");
        setAlertOkAction(() => () => setShowAlert(false));
      }
    } catch (err) {
      if (err.response?.status === 401) navigate("/");
    }
  };

  return (
    <div className="input-container">
      <input
        type="text"
        value={input}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={expectedAppln ? `Eg: ${expectedAppln}` : "Loading..."}
        className="input-box"
      />

      <ul className="validation-list">
        {validations.map((item, index) => {
          const passed = item.check(input);
          return (
            <li
              className={`validation-item fade-in ${passed ? "valid" : "invalid"}`}
              key={item.key}
              style={{ display: visibleIndexes.includes(index) ? "block" : "none" }}
            >
              <span className="icon">{passed ? "✅" : "❌"}</span>
              {item.text}
            </li>
          );
        })}
      </ul>

      <br />
      <div style={{ display: "flex", gap: "10px" }}>
        <Button
          name={"ADD"}
          style={{ width: "130px" }}
          onClick={handleConfirm}
          disabled={!validations.every((rule) => rule.check(input))}
        />
        <Button
          name={"CANCEL"}
          style={{ width: "130px", backgroundColor: "red" }}
          onClick={handleCancel}
        />
      </div>

      <Alert
        type={alertType}
        message={alertMessage}
        show={showAlert}
        okbutton={alertOkAction}
        cancelbutton={null}
      />
    </div>
  );
};

export default Input;
